<template>
  <div
    v-if="profile"
    class="pb-properties"
  >
    <EditorPanel class="pb-properties-panel">
      <template #default>
        <div class="pb-properties-panel-form">
          <InputWithLoader
            :label="translate('profile.properties.name')"
            :value="profile.name"
            :disabled="readonly"
            @input="set('name', $event)"
          />
          <BrandsField
            :value="profile.brand"
            :disabled="readonly"
            :alignment="propertiesPanelDropdownsAlignment"
            class="pb-quarter-margin-top"
            @input="set('brand', $event)"
          />
          <CategoriesField
            :label="translate('pb2.profilePropertiesView.profileCategoryLabel')"
            :value="profile.category ?? ''"
            :disabled="readonly"
            :alignment="propertiesPanelDropdownsAlignment"
            class="pb-quarter-margin-top"
            @input="set('category', $event)"
          />
          <UiField
            nolabel
            nofocus
            :label="translate('profile.properties.author')"
            class="pb-quarter-margin-top"
          >
            <span class="txt-muted txt-right">{{ profile.properties.creator }}</span>
          </UiField>
          <UiField
            v-if="userStore.isInternal"
            nolabel
            nofocus
            :label="translate('generic.owner')"
            :loading="!ownerSettled"
            class="pb-quarter-margin-top"
          >
            <AutoSpinner>
              <template v-if="ownerData">
                <span class="txt-muted txt-right">
                  {{ ownerData.displayName }}
                </span>
                <UiSeparator before />
                <BaseButton
                  class="hover-info focus-info"
                  tooltip="Copy email to clipboard"
                  @click="copyOwnerEmail"
                >
                  <UiIcon icon="copy" />
                </BaseButton>
                <UiSeparator />
                <BaseButton
                  tooltip="Send an email"
                  :to="`mailto:${ownerData.email}`"
                >
                  <UiIcon icon="message" />
                </BaseButton>
                <UiSeparator />
                <BaseButton
                  tooltip="See details in admin portal"
                  :to="config.urlTemplates.adminUser.template({ uid: ownerData.uid })"
                >
                  <UiIcon icon="user" />
                </BaseButton>
              </template>
              <span
                v-else
                class="txt-error txt-right"
              >
                Unable to load
              </span>
            </AutoSpinner>
          </UiField>
          <UiField
            nolabel
            nofocus
            :label="translate('profile.properties.createdDate')"
            class="pb-quarter-margin-top"
          >
            <span class="txt-muted txt-right">{{ formattedCreatedDate }}</span>
          </UiField>
          <template v-if="userStore.canManageProfiles">
            <InputWithLoader
              :type="INPUT_TYPE_SWITCH"
              :label="translate('pb2.profilePropertiesView.profileWorksWithWolfmixLabel')"
              :value="profile.worksWithWolfmix"
              :disabled="readonly"
              class="pb-quarter-margin-top"
              @input="set('worksWithWolfmix', $event)"
            />
            <InputWithLoader
              :type="INPUT_TYPE_SWITCH"
              :label="translate('generic.official')"
              :value="profile.isCertificated"
              :disabled="readonly"
              class="pb-quarter-margin-top"
              @input="set('isCertificated', $event)"
            />
          </template>
        </div>
        <ButtonGroup
          column
          class="pb-padding-top"
        >
          <ControlButton
            :aria-label="label"
            :to="downloadLinks.ssl2"
            :disabled="!enabled"
            :tooltip="label"
            target="_blank"
          >
            <UiIcon icon="download" /><span class="ellipsis">{{ translate('pb2.profilePropertiesView.downloadProfileLabel') }}</span>
          </ControlButton>
        </ButtonGroup>
        <!-- TODO: Add certification request -->
        <!-- TODO: Add worksWithWolfmix request -->
        <!--
        <ButtonGroup
          v-if="!userStore.canManageProfiles"
          row
          class="pb-half-padding-top"
        >
          <ButtonWithLoader tooltip="Request certification">
            <UiIcon icon="certified" color="success" />
            <span class="ellipsis">Request certification</span>
          </ButtonWithLoader>
          <ButtonWithLoader tooltip="Works with Wolfmix">
            <UiIcon icon="www" size="medium" class="pb-works-with-wolfmix-icon" />
            <span class="ellipsis">Works with Wolfmix</span>
          </ButtonWithLoader>
        </ButtonGroup>
        -->
        <ButtonGroup
          row
          class="pb-half-padding-top"
        >
          <ButtonWithLoader
            v-tooltip="{
              text: !profile.userCanCopy && translate('pb2.profilePropertiesView.missingProfileCopyPermissionTooltip'),
            }"
            :disabled="!profile.userCanCopy"
            @click="duplicateProfileAndRedirect"
          >
            <UiIcon icon="copy" /><span class="ellipsis">{{ translate('pb2.profilePropertiesView.duplicateProfileLabel') }}</span>
          </ButtonWithLoader>
          <ControlButton
            :tooltip="deleteButtonTitle"
            :disabled="!(profile.userCanDelete && profile.removable && !profile.isRemove)"
            class="btn-hover-error"
            @click="removeProfileClicked"
          >
            <UiIcon icon="trash" /><span class="ellipsis">{{ translate('pb2.profilePropertiesView.deleteProfileLabel') }}</span>
          </ControlButton>
        </ButtonGroup>
        <ConfirmDialog
          v-bind="deleteProfileConfirm"
          confirm-style="error"
          confirm-text="pb2.profilePropertiesView.profileDeleteConfirmLabel"
          :dialog-title="translate('pb2.profilePropertiesView.profileDeleteConfirmTitle')"
        >
          {{ translate('pb2.profilePropertiesView.profileDeleteConfirmMessage') }} "{{ profile.name }}" ?
        </ConfirmDialog>
      </template>
      <template #footer>
        <ButtonGroup
          padded
          row
        >
          <ControlButton
            :to="profileStore.portalLink"
            target="_blank"
          >
            <Asset
              src="/Images/Logos/SVG/Nicolaudie-cloud.svg"
              class="pb-profile-action-open-in-portal"
            />
            <span class="ellipsis">{{ translate('pb2.profilePropertiesView.openInPortalLabel') }}</span>
          </ControlButton>
        </ButtonGroup>
      </template>
    </EditorPanel>
    <ScrollableWrapper class="pb-properties-details">
      <div class="accordion pb-properties-details-accordion">
        <BaseCollapse
          v-bind="collapses.beams.control"
          :title="translate('pb2.profilePropertiesView.beamsCollapseTitle')"
        >
          <SelectFieldWithLoader
            v-if="displayProperties.beam_type"
            :label="translate('profile.properties.beam_type')"
            :options="config.properties.beamTypes"
            :value="profile.properties.beam_type"
            :disabled="readonly"
            alignment="right"
            @input="setProperty('beam_type', $event)"
          />
          <InputWithLoader
            v-if="displayProperties.beam_opening"
            :type="INPUT_TYPE_COUNTER"
            :label="translate('profile.properties.beam_opening')"
            :value="profile.properties.beam_opening"
            :disabled="readonly"
            min="0"
            max="180"
            autoselect
            class="pb-quarter-margin-top"
            @input="setProperty('beam_opening', $event)"
          />
          <InputWithLoader
            v-if="displayProperties.beam_opening2"
            :type="INPUT_TYPE_COUNTER"
            :label="translate('profile.properties.beam_opening2')"
            :value="profile.properties.beam_opening2"
            :disabled="readonly"
            min="0"
            max="180"
            autoselect
            class="pb-quarter-margin-top"
            @input="setProperty('beam_opening2', $event)"
          />
          <BeamsEditor
            v-model:beams="beams"
            v-model:clusters="clusters"
            :disabled="readonly || savingBeams"
            class="pb-half-margin-top"
          />
          <ButtonWithLoader
            icon="icon-save"
            :disabled="!(beamsTouched || clustersTouched)"
            @click="saveBeams"
          />
        </BaseCollapse>
        <BaseCollapse
          v-bind="collapses.resources.control"
          :title="translate('pb2.profilePropertiesView.resourcesCollapseTitle')"
        >
          <InputWithLoader
            :label="translate('profile.properties.manual_url')"
            :value="profile.properties.manual_url"
            :disabled="readonly"
            @input="setProperty('manual_url', $event)"
          />
          <InputWithLoader
            :label="translate('profile.properties.image_url')"
            :value="profile.properties.image_url"
            :disabled="readonly"
            class="pb-quarter-margin-top"
            @input="setProperty('image_url', $event)"
          />
        </BaseCollapse>
        <BaseCollapse
          v-bind="collapses.physical.control"
          :title="translate('pb2.profilePropertiesView.physicalCollapseTitle')"
        >
          <FamilySelectorWithLoader
            :value="profile.properties.fix_family"
            :disabled="readonly"
            @change="setFamily"
          />
          <div class="pb-row pb-properties-details-model">
            <div class="pb-half-margin-right pb-properties-details-model-selection">
              <SelectFieldWithLoader
                :label="translate('profile.properties.fix_type')"
                :options="typeOptions"
                :value="profile.properties.fix_type"
                :disabled="readonly"
                alignment="right"
                @input="setType"
              />
              <SelectFieldWithLoader
                :label="translate('profile.properties.fix_model')"
                :options="modelOptions"
                :value="profile.properties.file_object_3d_2"
                :disabled="readonly"
                alignment="right"
                search
                class="pb-quarter-margin-top"
                @input="setModel"
              />
            </div>
            <Asset
              class="pb-properties-details-model-preview-image"
              :src="modelIcon"
              alt="Model previsualization"
              placeload-size="xl"
              placeload-centered
              tabindex="0"
            />
          </div>
          <div class="pb-row pb-half-margin-top">
            <div class="pb-cell-1-2 pb-quarter-margin-right">
              <InputWithLoader
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.fix_width')"
                :value="profile.properties.fix_width"
                :disabled="readonly"
                autoselect
                @input="setProperty('fix_width', parseInt($event))"
              />
              <InputWithLoader
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.fix_height')"
                :value="profile.properties.fix_height"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('fix_height', parseInt($event))"
              />
            </div>
            <div class="pb-cell-1-2 pb-quarter-margin-left">
              <InputWithLoader
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.fix_depth')"
                :value="profile.properties.fix_depth"
                :disabled="readonly"
                autoselect
                @input="setProperty('fix_depth', parseInt($event))"
              />
              <InputWithLoader
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.fix_weight')"
                :value="profile.properties.fix_weight"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('fix_weight', parseInt($event))"
              />
            </div>
          </div>
        </BaseCollapse>
        <BaseCollapse
          :disabled="!hasLamp"
          v-bind="collapses.lamp.control"
          :title="translate('pb2.profilePropertiesView.lampCollapseTitle')"
        >
          <InputWithLoader
            v-if="displayProperties.lamp_power"
            :type="INPUT_TYPE_COUNTER"
            :label="translate('profile.properties.lamp_power')"
            :value="profile.properties.lamp_power"
            :disabled="readonly"
            autoselect
            @input="setProperty('lamp_power', parseInt($event))"
          />
          <InputWithLoader
            v-if="displayProperties.lamp_lumen"
            :type="INPUT_TYPE_COUNTER"
            :label="translate('profile.properties.lamp_lumen')"
            :value="profile.properties.lamp_lumen"
            :disabled="readonly"
            min="0"
            class="pb-quarter-margin-top"
            @input="setProperty('lamp_lumen', parseInt($event))"
          />
          <InputWithLoader
            v-if="displayProperties.lamp_ref"
            :label="translate('profile.properties.lamp_ref')"
            :value="profile.properties.lamp_ref"
            :disabled="readonly"
            class="pb-quarter-margin-top"
            @input="setProperty('lamp_ref', $event)"
          />
          <SelectFieldWithLoader
            v-if="displayProperties.lamp_type"
            :label="translate('profile.properties.lamp_type')"
            :options="config.properties.lampTypes"
            :value="profile.properties.lamp_type"
            :disabled="readonly"
            alignment="right"
            class="pb-quarter-margin-top"
            @input="setProperty('lamp_type', $event)"
          />
          <InputWithLoader
            v-if="displayProperties.lamp_temp"
            :type="INPUT_TYPE_COUNTER"
            :label="translate('profile.properties.lamp_temp')"
            :value="profile.properties.lamp_temp"
            :disabled="readonly"
            min="1000"
            max="12000"
            step="100"
            autoselect
            class="pb-quarter-margin-top"
            @input="setProperty('lamp_temp', $event)"
          />
          <ColorInputWithLoader
            v-if="displayProperties.color"
            :label="translate('profile.properties.color')"
            :value="profile.properties.color"
            :disabled="readonly"
            class="pb-quarter-margin-top"
            @input="setProperty('color', $event.dec)"
          />
        </BaseCollapse>
        <BaseCollapse
          :disabled="!hasPanTilt"
          v-bind="collapses.pt.control"
          :title="translate('pb2.profilePropertiesView.panTiltCollapseTitle')"
        >
          <div class="pb-row">
            <div class="pb-cell-1-2 pb-quarter-margin-right">
              {{ translate('pb2.profilePropertiesView.panTitle') }}
              <InputWithLoader
                v-if="displayProperties.pan_angle"
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.pan_angle')"
                :value="profile.properties.pan_angle"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('pan_angle', parseInt($event))"
              />
              <InputWithLoader
                v-if="displayProperties.pan_center"
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.pan_center')"
                :value="profile.properties.pan_center"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('pan_center', parseInt($event))"
              />
              <InputWithLoader
                v-if="displayProperties.time_pan"
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.time_pan')"
                :value="profile.properties.time_pan"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('time_pan', parseInt($event))"
              />
              <InputWithLoader
                v-if="displayProperties.inv_pan"
                :type="INPUT_TYPE_SWITCH"
                :label="translate('profile.properties.inv_pan')"
                :value="profile.properties.inv_pan"
                :disabled="readonly"
                class="pb-quarter-margin-top"
                @input="setProperty('inv_pan', $event)"
              />
            </div>
            <div class="pb-cell-1-2 pb-quarter-margin-left">
              {{ translate('pb2.profilePropertiesView.tiltTitle') }}
              <InputWithLoader
                v-if="displayProperties.tilt_angle"
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.tilt_angle')"
                :value="profile.properties.tilt_angle"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('tilt_angle', parseInt($event))"
              />
              <InputWithLoader
                v-if="displayProperties.tilt_center"
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.tilt_center')"
                :value="profile.properties.tilt_center"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('tilt_center', parseInt($event))"
              />
              <InputWithLoader
                v-if="displayProperties.time_tilt"
                :type="INPUT_TYPE_COUNTER"
                :label="translate('profile.properties.time_tilt')"
                :value="profile.properties.time_tilt"
                :disabled="readonly"
                autoselect
                class="pb-quarter-margin-top"
                @input="setProperty('time_tilt', parseInt($event))"
              />
              <InputWithLoader
                v-if="displayProperties.inv_tilt"
                :type="INPUT_TYPE_SWITCH"
                :label="translate('profile.properties.inv_tilt')"
                :value="profile.properties.inv_tilt"
                :disabled="readonly"
                class="pb-quarter-margin-top"
                @input="setProperty('inv_tilt', $event)"
              />
            </div>
          </div>
        </BaseCollapse>
        <BaseCollapse
          v-bind="collapses.rdm.control"
          :title="translate('pb2.profilePropertiesView.rdmCollapseTitle')"
        >
          <div class="pb-row">
            <div class="pb-cell-1-2 pb-quarter-margin-right">
              <InputWithLoader
                :label="translate('profile.properties.rdm_manufacturer_name')"
                :value="profile.properties.rdm_manufacturer_name"
                :disabled="readonly"
                @input="setProperty('rdm_manufacturer_name', $event)"
              />
              <InputWithLoader
                :label="translate('profile.properties.rdm_manufacturer_id')"
                :value="profile.properties.rdm_manufacturer_id"
                :disabled="readonly"
                class="pb-quarter-margin-top"
                @input="setProperty('rdm_manufacturer_id', $event)"
              />
            </div>
            <div class="pb-cell-1-2 pb-quarter-margin-left">
              <InputWithLoader
                :label="translate('profile.properties.rdm_fix_name')"
                :value="profile.properties.rdm_fix_name"
                :disabled="readonly"
                @input="setProperty('rdm_fix_name', $event)"
              />
              <InputWithLoader
                :label="translate('profile.properties.rdm_fix_id')"
                :value="profile.properties.rdm_fix_id"
                :disabled="readonly"
                class="pb-quarter-margin-top"
                @input="setProperty('rdm_fix_id', $event)"
              />
            </div>
          </div>
        </BaseCollapse>
        <BaseCollapse
          v-bind="collapses.permissions.control"
          :title="translate('pb2.profilePropertiesView.sharingCollapseTitle')"
        >
          <AccessRequestForm />
          <div class="pb-row pb-half-margin-top">
            <div class="pb-cell-1-1">
              <InputWithLoader
                :type="INPUT_TYPE_SWITCH"
                :label="translate('pb2.profilePropertiesView.profileCanBeDeletedLabel')"
                :value="profile.removable"
                :disabled="readonly || !profile.userCanManage"
                @input="set('removable', $event)"
              />
            </div>
            <!-- TODO: Add ownership transfer -->
            <!-- <div class="pb-cell-1-2 pb-half-margin-left">
              <ButtonGroup column>
                <ButtonWithLoader
                  :disabled="!profile.isMine"
                  :title="profile.isMine ? 'Change owner' : 'Request ownership'"
                >
                  <UiIcon icon="user-sml" />
                  <span class="ellipsis">
                    {{ profile.isMine ? 'Change owner' : 'Request ownership' }}
                  </span>
                </ButtonWithLoader>
              </ButtonGroup>
            </div> -->
          </div>
          <div class="pb-row">
            <div class="pb-cell-1-1">
              <ShareForm />
            </div>
          </div>
        </BaseCollapse>
      </div>
    </ScrollableWrapper>
  </div>
</template>

<script setup>
import {
  computed,
  markRaw,
  ref,
  toRef,
} from 'vue'

import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import UiSeparator from '@nicolaudie-group/ng-ui-kit/components/UiField/UiSeparator.vue'
import Asset from '@nicolaudie-group/ng-ui-kit/components/Asset/Asset.vue'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import BaseCollapse from '@nicolaudie-group/ng-ui-kit/components/BaseCollapse/BaseCollapse.vue'
import ScrollableWrapper from '@nicolaudie-group/ng-ui-kit/components/ScrollableWrapper/ScrollableWrapper.vue'
import { INPUT_TYPE_COUNTER, INPUT_TYPE_SWITCH } from '@nicolaudie-group/ng-ui-kit/components/BaseInput/symbols'
import AutoSpinner from '@nicolaudie-group/ng-ui-kit/components/AutoSpinner/AutoSpinner.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import useCloseOthers from '@nicolaudie-group/ng-ui-kit/composables/useCloseOthers'
import useConfirmDialog from '@nicolaudie-group/ng-ui-kit/composables/useConfirmDialog'
import useClipboard from '@nicolaudie-group/ng-ui-kit/composables/useClipboard'

import useRefSync from '@nicolaudie-group/ng-ui-kit/composables/useRefSync'
import useDevice from '@nicolaudie-group/ng-ui-kit/composables/useDevice'

import config from '@/config'
import useProfileStore from '@/stores/profile.store'
import useUserStore from '@/stores/user.store'
import useRouterStore from '@/stores/router.store'
import formatDate from '@/helpers/formatDate'
import hasChanged from '@/helpers/hasChanged'

import useAccordion from '@/composables/useAccordion'
import useTranslate from '@/composables/useTranslate'
import { useProfile, useBeams, useReadonly, useClusters } from '@/composables/useProfile'
import useProfileDownload from '@/composables/useProfileDownload'

import BeamsEditor from '@/components/Beams/BeamsEditor.vue'
import InputWithLoader from '@/components/InputWithLoader.vue'
import SelectFieldWithLoader from '@/components/SelectFieldWithLoader.vue'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import FamilySelectorWithLoader from '@/components/FamilySelectorWithLoader.vue'
import BrandsField from '@/components/BrandsField/BrandsField.vue'
import ButtonGroup from '@/components/ButtonGroup/ButtonGroup.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import EditorPanel from '@/components/EditorPanel/EditorPanel.vue'
import AccessRequestForm from '@/components/AccessRequestForm/AccessRequestForm.vue'
import ShareForm from '@/components/ShareForm/ShareForm.vue'
import ColorInputWithLoader from '@/components/ColorInputWithLoader.vue'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'
import routeNames from '@/router/names'
import CategoriesField from '@/components/BrandsField/CategoriesField.vue'

/*
const beams = fillBeams(
  SHAPES.RECT,
  1, // X
  1, // Y
  3, // Width
  3, // Heigh
  false, // Mirror horizontally
  false, // Mirror vertically
  { // Advanced options
    direction: 'horizontal', // Orientation of the assignment ("horizontal" or "vertical")
    alternate: false, // Alternate the direction of the assignment
    startId: 1, // ID of the first beam
    offset: 0, // Shift the assignment by this amount
  },
),
*/

const { translate } = useTranslate()

const profileStore = useProfileStore()
const userStore = useUserStore()
const routerStore = useRouterStore()

const profile = useProfile()
const readonly = useReadonly()

const { isBiggerThanTablet } = useDevice()
const propertiesPanelDropdownsAlignment = computed(() => (isBiggerThanTablet.value ? 'right' : 'left'))

const formattedCreatedDate = computed(() => formatDate(profile.value.createdDate))

const edit = (payload) => profileStore.edit(payload)
const set = (key, value) => edit({ [key]: value })
const setProperty = (key, value) => set('properties', { [key]: value })

const savingBeams = ref(false)

const beams = useRefSync(useBeams())
const lastSavedBeams = ref(beams.value)
const beamsTouched = computed(() => hasChanged(beams.value, lastSavedBeams.value))

const clusters = useRefSync(useClusters())
const lastSavedClusters = ref(clusters.value)
const clustersTouched = computed(() => hasChanged(clusters.value, lastSavedClusters.value))

const saveBeams = async () => {
  const properties =  {}

  const beamsValue = beams.value
  const clustersValue = clusters.value

  if (beamsTouched.value) properties.beams = beamsValue
  if (clustersTouched.value) properties.clusters = clustersValue

  savingBeams.value = true
  await set('properties', properties)
  savingBeams.value = false

  lastSavedBeams.value = beamsValue
  lastSavedClusters.value = clustersValue
}

async function setFamily(value) {
  // If the family actually changed
  if (value !== profile.value.properties.fix_family) {
    // Find the family
    const family = config.properties.modelsFamilies.find((
      (modelFamily) => modelFamily.value === value
    ))
    // TODO: The following should be done in backend instead
    // Select the first type by default
    const type = family.types[0]
    // Select the first model by default
    const model = type.models[0]
    await edit({
      properties: {
        fix_family: family.value,
        fix_type: type.value,
        file_object_3d_2: model.value,
      },
    })
  }
}

async function setType(value) {
  // If the type actually changed
  if (value !== profile.value.properties.fix_type) {
    // Find the type
    const type = profileStore.family.types.find((
      (modelType) => modelType.value === value
    ))
    // TODO: The following should be done in backend instead
    // Select the first model by default
    const model = type.models[0]
    await edit({
      properties: {
        fix_type: type.value,
        file_object_3d_2: model.value,
      },
    })
  }
}

async function setModel(value) {
  // If the type actually changed
  if (value !== profile.value.properties.file_object_3d_2) {
    // Find the type
    const model = profileStore.type.models.find((
      (modelItem) => modelItem.value === value
    ))
    await edit({
      properties: {
        file_object_3d_2: model.value,
      },
    })
  }
}

const modelIcon = computed(() => profileStore.model?.asset?.src)

const typeOptions = computed(() => markRaw(profileStore.family.types))
const modelOptions = computed(() => markRaw(
  profileStore.type ? profileStore.type.models : [],
))

const displayProperties = computed(() => profileStore.family.properties)

const hasPanTilt = computed(() => (
  displayProperties.value.pan_angle
  || displayProperties.value.pan_center
  || displayProperties.value.time_pan
  || displayProperties.value.inv_pan
  || displayProperties.value.tilt_angle
  || displayProperties.value.tilt_center
  || displayProperties.value.time_tilt
  || displayProperties.value.inv_tilt
))

const hasLamp = computed(() => (
  displayProperties.value.lamp_power
  || displayProperties.value.lamp_lumen
  || displayProperties.value.lamp_ref
  || displayProperties.value.lamp_type
  || displayProperties.value.lamp_life
  || displayProperties.value.lamp_temp
  || displayProperties.value.color
))

const { collapses } = useAccordion([
  'beams',
  'resources',
  'physical',
  'lamp',
  'pt',
  'rdm',
  'permissions',
], { query: 'edit' })

const deleteButtonTitle = computed(() => {
  if (profile.value) {
    const { userCanDelete, removable, isRemove } = profile.value
    if (!userCanDelete) {
      return translate('pb2.profilePropertiesView.missingProfileDeletePermissionTooltip')
    }
    if (!removable) {
      return translate('pb2.profilePropertiesView.profileDeleteDisabledTooltip')
    }
    if (isRemove) {
      return translate('pb2.profilePropertiesView.profileAlreadyDeletedTooltip')
    }
  }
  return null
})

async function duplicateProfileAndRedirect() {
  const { uid } = await profileStore.duplicate()
  await routerStore.push({
    name: routeNames.Properties,
    params: {
      profileUid: uid,
    },
  })
}

async function deleteProfile() {
  await profileStore.delete()
}

const {
  control: deleteProfileConfirm,
  open: openDeleteProfileConfirm,
  cancel,
  state,
} = useConfirmDialog(deleteProfile)
useCloseOthers(cancel, state)

function removeProfileClicked() {
  return openDeleteProfileConfirm()
}

const {
  downloadLinks,
  label,
  enabled,
} = useProfileDownload()

const { copy } = useClipboard()

const ownerStatus = toRef(profileStore, 'ownerStatus')
const ownerData = toRef(profileStore, 'ownerData')

const { settled: ownerSettled } = useStatus(ownerStatus)

function copyOwnerEmail() {
  copy(ownerData.value?.email)
}

</script>

<style lang="scss">
@use "sass:math";
@use "@nicolaudie-group/ng-ui-kit/styles/defines/_fonts.define.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/defines/_colors.define.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/defines/_config.define.scss";

@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_flexbox.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_generic.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_responsive.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_theme.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_animations.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_transitions.mixin.scss";

@use '/src/styles/_pb.scss';

.pb-properties {
  @include flexbox.flexbox();
  height: 100%;

  .pb-properties-panel {
    @include flexbox.flex-basis(math.div(100%, 3));
    border-right: 1px solid colors.$color-default--gray-border;
    // padding: pb.$spacing * 2;
    max-width: 600px;
  }

  .pb-properties-details {
    @include flexbox.flex-basis(math.div(100%, 3) * 2);
    @include flexbox.flex-grow(1);
    padding: pb.$spacing * 2;
  }

  .pb-properties-details-accordion {
    max-width: 800px;
    margin: auto;
  }

  .pb-profile-action-open-in-portal {
    height: 100%;
  }
}

.pb-properties-details-model {
  @include flexbox.align-items(center);
  @include flexbox.justify-content(stretch);

  .pb-properties-details-model-selection {
    @include flexbox.flex-grow(1);
    @include flexbox.flex-basis(100%);
  }

  .pb-properties-details-model-selection-list-preview {
    // Reduce padding around the preview image
    margin-left: -5px;
    margin-top: -5px;
    margin-bottom: -5px;
    // Increase the preview image size
    max-width: 4em;
    max-height: 4em;
  }

  .pb-properties-details-model-preview-image {
    width: 88px;
    height: 88px;
    transform-origin: right center;
    background-color: colors.$color-default--gray-back;
    border-radius: config.$border-radius--default-medium;
    transition: transform .2s ease, box-shadow .2s ease;
    outline: none;
  }

  .pb-properties-details-model-preview-image:hover,
  .pb-properties-details-model-preview-image:focus {
    transform: scale(3);
    box-shadow: 0 0 2px rgba(0, 0, 0, .3);
  }
}
</style>
